import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Car,
  Download,
  Upload,
  Filter,
  Home,
  Layers,
  Plus,
  Store,
  UserCheck,
  Users,
  X,
  Phone,
  Copy,
  Shield,
  DoorOpen,
  DoorClosed,
  Laptop,
  Award,
  FlaskConical,
  Tag,
  Trash2,
} from "lucide-react";
import styles from "./UserManagement.module.css";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";
import StatCard from "../../../components/ui/StatCard";
import Button from "../../../components/ui/Button";
import ActionButtons from "../../../components/common/ActionButtons";
import Modal from "../../../components/ui/Modal";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import { toast } from "react-toastify";
import InviteUserModal from "../../../components/users/InviteUserModal";
import { Link2 } from "lucide-react";
import AppUserForm from "../../../components/users/AppUserForm";
import FilterBar from "../../../components/common/FilterBar";
import Skeleton from "../../../components/ui/Skeleton";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import SearchInput from "../../../components/ui/SearchInput";

import {
  APP_SETTINGS_EVENT,
  defaultAppSettings,
  getAppSettings,
  getPrimaryRoles,
  getSystemRoles,
} from "../../../services/appSettingsService";
import { getCurrentRoleKey, getStoredAuthUser, normalizeRoleKey } from "../../../services/authService";
import { deleteUser, getUsers } from "../../../services/userService";
import { getSocieties } from "../../../services/societyService";
import API from "../../../services/api";

const AVATAR_PALETTES = [
  { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" }, // Blue
  { bg: "#f5f3ff", text: "#7c3aed", border: "#ddd6fe" }, // Purple
  { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" }, // Green
  { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" }, // Orange
  { bg: "#fdf2f8", text: "#db2777", border: "#fbcfe8" }, // Pink
  { bg: "#f0fdfa", text: "#0d9488", border: "#99f6e4" }, // Teal
];

const getInitials = (name) => {
  if (!name) return "U";
  // Remove parenthesized words like (Approver), (Host), (QA Owner Persona) and special characters
  const cleaned = name.replace(/\([^)]*\)/g, "").replace(/[^a-zA-Z0-9\s]/g, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) {
    const raw = name.replace(/[^a-zA-Z0-9]/g, "");
    return raw.slice(0, 2).toUpperCase() || "U";
  }
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarStyle = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
};

const formatPhoneNumber = (val) => {
  if (!val) return "";
  const str = String(val).replace(/\D/g, "");
  if (str.length === 10) {
    return `${str.slice(0, 5)}\u00A0${str.slice(5)}`;
  }
  return val;
};

const formatRoleName = (role) => {
  if (!role) return "User";
  const raw = String(role).trim();

  const knownMap = {
    department_head: "Department Head",
    "department-head": "Department Head",
    receptionist: "Receptionist",
    security: "Security Guard",
    guard: "Security Guard",
    security_guard: "Security Guard",
    "security-guard": "Security Guard",
    employee: "Employee",
    owner: "Owner",
    tenant: "Tenant",
    family_member: "Family Member",
    super_admin: "Super Admin",
    "super-admin": "Super Admin",
    society_admin: "Society Admin",
    "society-admin": "Society Admin",
    admin: "Admin",
    director: "Director",
    manager: "Manager",
    plant_manager: "Plant Manager",
    "plant-manager": "Plant Manager",
  };

  const lower = raw.toLowerCase();
  if (knownMap[lower]) return knownMap[lower];

  return raw
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const ALLOWED_DELETE_ROLE_KEYS = ["super-admin", "admin", "society-admin"];
const UserManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isResidentsView = location.pathname.startsWith("/users") || location.pathname.startsWith("/residents") || location.pathname === "/user-management";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [appSettings, setAppSettings] = useState(defaultAppSettings);
  const [roleOptions, setRoleOptions] = useState(getPrimaryRoles());
  const [filters, setFilters] = useState({
    os_type: [],
    role: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    os_type: [],
    role: [],
  });
  const [societies, setSocieties] = useState([]);
  const [selectedSocietyId, setSelectedSocietyId] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab");
  const roleParam = searchParams.get("role");
  const typeParam = searchParams.get("type"); // Residential | Commercial | Industrial | Hybrid

  const societyTypeMap = useMemo(() => {
    const map = new Map();
    societies.forEach((s) => {
      map.set(Number(s.society_id), String(s.society_type || "").toLowerCase());
    });
    return map;
  }, [societies]);

  const scopedSocieties = useMemo(() => {
    if (!typeParam) return societies;
    const target = typeParam.toLowerCase();
    const filtered = societies.filter((s) => {
      const st = String(s.society_type || "").toLowerCase();
      if (target === "residential") return st === "residential" || st.includes("hybrid");
      if (target === "commercial") return st === "commercial" || st.includes("hybrid");
      if (target === "industrial") return st === "industrial";
      if (target === "hybrid" || target.includes("hybrid")) return st.includes("hybrid");
      return st === target;
    });
    return filtered.length > 0 ? filtered : societies;
  }, [societies, typeParam]);

  const selectedSociety = useMemo(() => {
    return societies.find((s) => String(s.society_id) === String(selectedSocietyId)) || null;
  }, [societies, selectedSocietyId]);

  const effectivePropertyType = useMemo(() => {
    if (selectedSociety?.society_type) return selectedSociety.society_type;
    return typeParam || "";
  }, [selectedSociety, typeParam]);

  const isIndustrial = effectivePropertyType.toLowerCase() === "industrial";
  const isCommercial = effectivePropertyType.toLowerCase() === "commercial";
  const isResidential = effectivePropertyType.toLowerCase() === "residential";
  const isHybrid = effectivePropertyType.toLowerCase() === "hybrid" || effectivePropertyType.toLowerCase().includes("hybrid");

  const getDisplayTitles = () => {
    if (isIndustrial) {
      return {
        plural: "Industrial Personnel & Staff",
        singular: "Personnel",
        subtitle: "Manage industrial directors, plant managers, engineers, staff & workstations"
      };
    }
    if (isCommercial) {
      return {
        plural: "Commercial Tenants & Staff",
        singular: "Tenant / Staff",
        subtitle: "Manage commercial shop owners, office managers, tenants & unit staff"
      };
    }
    if (isResidential) {
      return {
        plural: "Residents & Families",
        singular: "Resident",
        subtitle: "Manage flat owners, tenants, and family members"
      };
    }
    if (isHybrid) {
      return {
        plural: "Hybrid Property Users",
        singular: "Occupant",
        subtitle: "Manage mixed residential and commercial occupants"
      };
    }
    if (roleParam === "admin") return { plural: "Admins", singular: "Admin", subtitle: "Manage system administrators and supervisors" };
    if (roleParam === "guard") return { plural: "Security Guards", singular: "Guard", subtitle: "Manage gate security guards, shifts, and agencies" };
    if (!roleParam) return {
      plural: "People Directory",
      singular: "User",
      subtitle: "Manage all residents, commercial tenants, and industrial personnel across properties"
    };

    const match = appSettings?.roles?.find(r => r.id === roleParam || r.name.toLowerCase() === roleParam.toLowerCase());
    if (match) {
      return {
        plural: match.name + (match.name.endsWith('s') ? '' : 's'),
        singular: match.name,
        subtitle: `Manage ${match.name.toLowerCase()} accounts and assignments`
      };
    }
    return {
      plural: "People Directory",
      singular: "User",
      subtitle: "Manage and view all users across all property structures"
    };
  };
  const { plural: displayTitle, singular: singularTitle, subtitle: displaySubtitle } = getDisplayTitles();

  const [approvalFilter, setApprovalFilter] = useState(tabParam === "pending" ? "PENDING" : "ALL");

  useEffect(() => {
    if (tabParam === "pending") setApprovalFilter("PENDING");
  }, [tabParam]);

  const appUsers = useMemo(
    () => users,
    [users]
  );

  const typeCounts = useMemo(() => {
    const counts = {
      all: 0,
      residential: 0,
      commercial: 0,
      industrial: 0,
      hybrid: 0,
    };
    appUsers.forEach((u) => {
      if (u.primary_role === "System" && !u.society_id) return;
      counts.all += 1;
      const socType = (societyTypeMap.get(Number(u.society_id)) || "").toLowerCase();
      if (socType === "industrial") {
        counts.industrial += 1;
      } else if (socType === "commercial") {
        counts.commercial += 1;
      } else if (socType.includes("hybrid")) {
        counts.hybrid += 1;
      } else if (socType === "residential") {
        counts.residential += 1;
      } else {
        const role = String(u.secondary_role || u.primary_role || "").toLowerCase();
        if (role.includes("industrial") || role.includes("plant") || role.includes("employee")) {
          counts.industrial += 1;
        } else if (role.includes("commercial") || role.includes("office")) {
          counts.commercial += 1;
        } else {
          counts.residential += 1;
        }
      }
    });
    return counts;
  }, [appUsers, societyTypeMap]);

  const handleSelectPropertyType = (typeKey) => {
    const params = new URLSearchParams(location.search);
    if (typeKey) {
      params.set("type", typeKey);
    } else {
      params.delete("type");
    }
    if (selectedSocietyId && typeKey) {
      const currentSocType = (societyTypeMap.get(Number(selectedSocietyId)) || "").toLowerCase();
      const target = typeKey.toLowerCase();
      const matches =
        (target === "residential" && (currentSocType === "residential" || currentSocType.includes("hybrid"))) ||
        (target === "commercial" && (currentSocType === "commercial" || currentSocType.includes("hybrid"))) ||
        (target === "industrial" && currentSocType === "industrial") ||
        (target.includes("hybrid") && currentSocType.includes("hybrid"));
      if (!matches) {
        setSelectedSocietyId("");
      }
    }
    const qStr = params.toString();
    navigate(`/users${qStr ? `?${qStr}` : ""}`, { replace: true });
  };

  const filtersConfig = useMemo(() => {
    if (isIndustrial) {
      return [
        { key: "os_type", label: "OS Type", options: ["Android", "iOS"] },
        {
          key: "role",
          label: "Role",
          options: [
            "Industrial Director",
            "Plant Manager",
            "Head of Department (HOD)",
            "Team Lead (TL)",
            "Employee",
            "Gate Receptionist",
            "Receptionist",
            "Industrial Receptionist",
            "Staff",
            "Security Guard",
          ],
        },
      ];
    }
    if (isCommercial) {
      return [
        { key: "os_type", label: "OS Type", options: ["Android", "iOS"] },
        {
          key: "role",
          label: "Role",
          options: [
            "Commercial Owner",
            "Commercial Tenant",
            "Office Manager",
            "Staff",
            "Security Guard",
          ],
        },
      ];
    }
    if (isHybrid) {
      return [
        { key: "os_type", label: "OS Type", options: ["Android", "iOS"] },
        {
          key: "role",
          label: "Role",
          options: [
            "Owner",
            "Tenant",
            "Commercial Owner",
            "Commercial Tenant",
            "Office Manager",
            "Staff",
            "Security Guard",
          ],
        },
      ];
    }
    if (roleParam === "admin") {
      const depts = [...new Set(appUsers.map(u => u.role_details?.department).filter(Boolean))];
      const levels = [...new Set(appUsers.map(u => u.role_details?.access_level).filter(Boolean))];
      return [
        { key: "role", label: "User Type", options: getSystemRoles() },
        { key: "department", label: "Department", options: depts },
        { key: "access_level", label: "Access Level", options: levels },
      ];
    }
    if (roleParam === "guard") {
      const shifts = [...new Set(appUsers.map(u => u.role_details?.shift).filter(Boolean))];
      const agencies = [...new Set(appUsers.map(u => u.role_details?.agency).filter(Boolean))];
      return [
        { key: "role", label: "Role", options: ["Security Guard"] },
        { key: "shift", label: "Shift Timing", options: shifts },
        { key: "agency", label: "Agency", options: agencies },
      ];
    }
    return [
      { key: "os_type", label: "OS Type", options: ["Android", "iOS"] },
      {
        key: "role",
        label: "User Role",
        options: [
          ...new Set([
            ...roleOptions,
            "Owner", "Resident", "Tenant",
            "Industrial Director", "Plant Manager", "Head of Department (HOD)", "Employee",
            "Commercial Owner", "Commercial Tenant", "Office Manager",
            "Security Guard"
          ])
        ]
      },
    ];
  }, [typeParam, roleParam, appUsers, roleOptions, isIndustrial, isCommercial, isHybrid]);

  const currentUser = getStoredAuthUser();
  const currentUserRoleKey = getCurrentRoleKey();
  const currentUserSocietyId = currentUser?.society_id ? String(currentUser.society_id) : "";
  const canDelete = ALLOWED_DELETE_ROLE_KEYS.includes(currentUserRoleKey);
  const canManageResidents = currentUserRoleKey !== "society-admin";
  const canWriteUsers = ALLOWED_DELETE_ROLE_KEYS.includes(currentUserRoleKey);

  console.log("Current User Role:", currentUserRoleKey);

  const canPickSociety = useMemo(
    () => ["super-admin", "admin"].includes(currentUserRoleKey),
    [currentUserRoleKey]
  );

  const effectiveSocietyId = canPickSociety ? selectedSocietyId : currentUserSocietyId;

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getUsers(effectiveSocietyId || null);
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [effectiveSocietyId]);

  const fetchSocieties = useCallback(async () => {
    try {
      const data = await getSocieties();
      setSocieties(data || []);

      if (!canPickSociety && currentUserSocietyId) {
        setSelectedSocietyId(String(currentUserSocietyId));
      }
    } catch (err) {
      console.error(err);
    }
  }, [canPickSociety, currentUserSocietyId, selectedSocietyId]);

  useEffect(() => {
    fetchSocieties();
  }, [fetchSocieties]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const syncRoles = async () => {
      const settings = await getAppSettings();
      const residentRoleNames = getPrimaryRoles();

      setRoleOptions(residentRoleNames);
      setAppSettings(settings);
    };

    syncRoles();
    window.addEventListener(APP_SETTINGS_EVENT, syncRoles);

    return () => {
      window.removeEventListener(APP_SETTINGS_EVENT, syncRoles);
    };
  }, []);

  const hasInitializedRoles = useRef(false);

  useEffect(() => {
    hasInitializedRoles.current = false;
  }, [roleParam]);

  useEffect(() => {
    if (!isResidentsView || hasInitializedRoles.current) return;

    // Wait until roles sync is complete. If roleOptions is completely empty but sync finished, 
    // we should still initialize. We can rely on a small timeout or just initialize anyway.
    // Actually, getSystemRoles() might have items even if roleOptions is empty.
    let initialRoles = roleOptions;
    if (roleParam === "admin") {
      initialRoles = getSystemRoles();
    } else if (!roleParam) {
      initialRoles = [];
    } else {
      // It's a specific role (like owner, tenant, guard, etc.)
      const match = appSettings?.roles?.find(r => r.id === roleParam || r.name.toLowerCase() === roleParam.toLowerCase());
      if (match) {
        if (match.id === "owner") {
          // Keep legacy behavior where Owner also searches for Resident
          initialRoles = ["Owner", "Resident"];
        } else {
          initialRoles = [match.name];
        }
      } else {
        initialRoles = [roleParam];
      }
    }

    const next = { ...appliedFilters, role: initialRoles };
    setFilters(next);
    setAppliedFilters(next);
    hasInitializedRoles.current = true;
  }, [appliedFilters, isResidentsView, roleOptions, roleParam]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    const empty = {};
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const filteredUsers = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();
    const activeType = (effectivePropertyType || typeParam || "").toLowerCase();

    return appUsers.filter((user) => {
      // 1. System users are managed exclusively on /settings page
      if (user.primary_role === "System" && !user.society_id) {
        return false;
      }

      // 2. Property Type Filtering (Residential, Commercial, Industrial, Hybrid)
      if (activeType) {
        const userSocType = (societyTypeMap.get(Number(user.society_id)) || "").toLowerCase();
        if (userSocType) {
          if (activeType === "residential" && !(userSocType === "residential" || userSocType.includes("hybrid"))) return false;
          if (activeType === "commercial" && !(userSocType === "commercial" || userSocType.includes("hybrid"))) return false;
          if (activeType === "industrial" && userSocType !== "industrial") return false;
          if ((activeType === "hybrid" || activeType.includes("hybrid")) && !userSocType.includes("hybrid")) return false;
        } else {
          const userRole = user.secondary_role || user.primary_role;
          const roleDef = appSettings?.roles?.find(
            (r) => String(r.name).toLowerCase() === String(userRole).toLowerCase()
          );
          const rolePropertyTypes = (roleDef?.propertyTypes || []).map((t) => t.toLowerCase());
          if (rolePropertyTypes.length > 0) {
            const matchesRole = rolePropertyTypes.some((pt) => {
              if (activeType === "residential") return pt === "residential" || pt.includes("hybrid");
              if (activeType === "commercial") return pt === "commercial" || pt.includes("hybrid");
              if (activeType === "industrial") return pt === "industrial";
              if (activeType === "hybrid" || activeType.includes("hybrid")) return pt.includes("hybrid") || pt === "residential" || pt === "commercial";
              return pt === activeType;
            });
            if (!matchesRole) return false;
          }
        }
      }

      // 3. Approval Tab Filtering
      if (approvalFilter === "PENDING") {
        if (user.approval_status !== "PENDING") return false;
      } else {
        // In "ALL" tab, hide pending users so they strictly live in the pending queue
        if (user.approval_status === "PENDING") return false;
      }

      // 4. Explicit Role & OS Filtering
      const userPrimary = normalizeRoleKey(user.primary_role);
      const userSystem = normalizeRoleKey(user.system_role);

      const matchesFilters = filtersConfig.every(filter => {
        const key = filter.key;
        const selected = appliedFilters[key] || [];
        if (selected.length === 0) return true;

        if (key === "role") {
          const allowed = selected.map(t => normalizeRoleKey(t));
          return allowed.includes(userPrimary) || allowed.includes(userSystem);
        }
        if (key === "os_type") {
          return selected.includes(user.os_type);
        }
        if (key === "department") {
          return selected.includes(user.role_details?.department);
        }
        if (key === "access_level") {
          return selected.includes(user.role_details?.access_level);
        }
        if (key === "shift") {
          return selected.includes(user.role_details?.shift);
        }
        if (key === "agency") {
          return selected.includes(user.role_details?.agency);
        }
        return true;
      });

      // If we are in the PENDING tab, we bypass the role filter so admins can always see the queue
      // even if the user registered with a default role not in the filter dropdown.
      if (!matchesFilters && approvalFilter !== "PENDING") return false;

      // 5. Search query
      if (!q) return true;

      const haystack = [
        user.full_name,
        user.email_id,
        user.mobile_number,
        user.system_role || user.primary_role,
        user.secondary_role,
        user.role_details?.department,
        user.role_details?.designation,
        user.role_details?.desk_code,
        user.role_details?.cabin_code,
        user.role_details?.agency,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [appUsers, appliedFilters, searchText, approvalFilter, filtersConfig, effectivePropertyType, typeParam, societyTypeMap, appSettings]);

  const stats = useMemo(
    () => {
      const total = filteredUsers.length;
      const active = filteredUsers.filter(u => u.is_active || String(u.status).toLowerCase() === 'active').length;
      const family = filteredUsers.reduce((sum, u) => sum + Number(u.family_count || 0), 0);
      const vehicles = filteredUsers.reduce((sum, u) => sum + Number(u.vehicle_count || 0), 0);
      const onDuty = filteredUsers.filter(u => u.role_details?.duty_status === 'On Duty' || u.role_details?.duty_status === 'Active').length;
      const offDuty = filteredUsers.filter(u => u.role_details?.duty_status === 'Off Duty').length;
      const unitsOrDesks = filteredUsers.filter(u => (u.flat_mappings && u.flat_mappings.length > 0) || u.role_details?.desk_code).length;
      const depts = new Set(filteredUsers.map(u => u.role_details?.department).filter(Boolean)).size;

      return {
        total,
        active,
        inactive: total - active,
        family,
        vehicles,
        onDuty,
        offDuty,
        unitsOrDesks,
        depts,
      };
    },
    [filteredUsers]
  );

  const exportCsv = useCallback(() => {
    try {
      void API.post("/audit-logs/event", {
        module: "USER",
        action: "RESIDENTS_EXPORT",
        description: "Residents exported",
        new_value: {
          society_id: effectiveSocietyId || null,
          count: filteredUsers.length,
          view: isResidentsView ? "residents" : "users",
        },
      });
    } catch (_) {
      // ignore audit log failures for export
    }

    const rows = filteredUsers.map((u) => ({
      Name: u.full_name || "",
      Email: u.email_id || "",
      Phone: u.mobile_number || "",
      "App Role": u.system_role || u.primary_role || "",
      Gender: u.gender || "",
      "OS Type": u.os_type || "",
      "Society ID": u.society_id ?? "",
      Units: (() => {
        const mappings = Array.isArray(u.flat_mappings) ? u.flat_mappings : [];
        if (!mappings.length) return "";
        return mappings
          .map((m) => {
            const tower = m.tower_name ? String(m.tower_name) : "";
            const flat = m.flat_number || m.flat_id;
            const type = m.ownership_type || "";
            const unit = tower ? `${tower}-${flat}` : String(flat || "");
            return type ? `${unit} (${type})` : unit;
          })
          .filter(Boolean)
          .join(" | ");
      })(),
      "Family Members": (() => {
        const list = Array.isArray(u.family_members) ? u.family_members : [];
        if (!list.length) return "";
        return list
          .map((m) => {
            const name = m.member_name || "";
            const relation = m.relation || "";
            const type = m.member_type || "";
            return [name, relation, type].filter(Boolean).join(":");
          })
          .filter(Boolean)
          .join(" | ");
      })(),
      Vehicles: (() => {
        const list = Array.isArray(u.vehicles) ? u.vehicles : [];
        if (!list.length) return "";
        return list
          .map((v) => {
            const num = v.vehicle_number || "";
            const type = v.vehicle_type || "";
            const model = v.model || "";
            const color = v.color || "";
            const slot = v.parking_slot || "";
            return [num, type, model, color, slot].filter(Boolean).join(":");
          })
          .filter(Boolean)
          .join(" | ");
      })(),
      Status: u.is_active === 0 ? "Inactive" : "Active",
    }));

    const headers = Object.keys(rows[0] || { Name: "", Email: "" });
    const escape = (value) => `"${String(value ?? "").replaceAll("\"", "\"\"")}"`;

    const csv = [headers.join(",")]
      .concat(rows.map((r) => headers.map((h) => escape(r[h])).join(",")))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = isResidentsView ? "residents.csv" : "users.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [effectiveSocietyId, filteredUsers, isResidentsView]);

  const handleDeleteClick = (row) => {
    setDeleteTarget({
      row,
      type: singularTitle || "User",
      id: row.user_id,
      name: row.full_name || row.email_id || `User #${row.user_id}`,
      canDelete,
      actionLabel: "Deactivate",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.row) return;
    try {
      await deleteUser(deleteTarget.row.user_id);
      toast.success(`${deleteTarget.type || "User"} deactivated successfully.`);
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to deactivate user.");
    }
  };

  const selectedUsersList = useMemo(() => {
    return filteredUsers.filter((u) => selectedUserIds.includes(u.user_id));
  }, [filteredUsers, selectedUserIds]);

  const handleToggleSelectAll = useCallback(() => {
    if (selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u.user_id));
    }
  }, [filteredUsers, selectedUserIds.length]);

  const handleToggleSelectUser = useCallback((id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleConfirmBulkDelete = async () => {
    if (!selectedUserIds.length) return;
    try {
      setBulkDeleting(true);
      await Promise.all(selectedUserIds.map((id) => deleteUser(id)));
      toast.success(`Successfully deactivated ${selectedUserIds.length} ${singularTitle || "user"}(s).`);
      setSelectedUserIds([]);
      setIsBulkDeleteModalOpen(false);
      fetchUsers();
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error(err?.message || "Failed to deactivate selected users.");
      fetchUsers();
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    setSelectedUserIds([]);
  }, [selectedSocietyId, searchText]);

  const handleApprove = async (row) => {
    if (!window.confirm(`Approve this ${singularTitle.toLowerCase()}?`)) return;
    try {
      await API.patch(`/users/${row.user_id}/approve`);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReject = async (row) => {
    if (!window.confirm(`Reject this ${singularTitle.toLowerCase()}?`)) return;
    try {
      await API.patch(`/users/${row.user_id}/reject`);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (row) => {
    setSelectedUser(row);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleView = (row) => {
    setSelectedUser(row);
    setViewMode(true);
    setOpenModal(true);
  };

  const columns = useMemo(() => {
    if (isResidentsView) {
      return [
        ...(canDelete ? [{
          header: (
            <input
              type="checkbox"
              checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
              onChange={handleToggleSelectAll}
              title="Select All"
              style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#2563eb" }}
            />
          ),
          width: "48px",
          minWidth: "48px",
          render: (row) => (
            <input
              type="checkbox"
              checked={selectedUserIds.includes(row.user_id)}
              onChange={() => handleToggleSelectUser(row.user_id)}
              style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#2563eb" }}
            />
          ),
        }] : []),
        {
          header: "User / Personnel",
          width: "270px",
          minWidth: "250px",
          render: (row) => {
            const avatarStyle = getAvatarStyle(row.full_name);
            const initials = getInitials(row.full_name);

            return (
              <div className={styles.residentCell}>
                <div
                  className={styles.avatarInitials}
                  style={{
                    backgroundColor: avatarStyle.bg,
                    color: avatarStyle.text,
                    border: `1px solid ${avatarStyle.border}`,
                  }}
                >
                  {initials}
                </div>
                <div className={styles.residentMeta}>
                  <div className={styles.nameRow}>
                    <span className={styles.residentName}>{row.full_name || "—"}</span>
                    {row.is_tester && (
                      <span className={styles.testerBadge} title="Static OTP bypass tester account">
                        <FlaskConical size={10} /> Tester
                      </span>
                    )}
                  </div>
                  <div className={styles.emailText} title={row.email_id || ""}>
                    {row.email_id || "No email registered"}
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          header: "Property",
          width: "190px",
          minWidth: "170px",
          render: (row) => {
            const soc = societies.find((s) => Number(s.society_id) === Number(row.society_id));
            const userSocType = (societyTypeMap.get(Number(row.society_id)) || soc?.society_type || "").toLowerCase();

            return (
              <div className={styles.propertyCell}>
                <div className={styles.propName}>
                  <Building2 size={13} color="#0284c7" />
                  <span>{soc?.society_name || (row.society_id ? `Property #${row.society_id}` : "Unassigned")}</span>
                </div>
                {userSocType && (
                  <span
                    className={[
                      styles.propArchetypePill,
                      userSocType === "industrial"
                        ? styles.archetypeIndustrial
                        : userSocType === "commercial"
                        ? styles.archetypeCommercial
                        : styles.archetypeResidential,
                    ].join(" ")}
                  >
                    {userSocType}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          header: "Unit / Flat",
          width: "170px",
          minWidth: "150px",
          render: (row) => {
            const mappings = Array.isArray(row.flat_mappings) ? row.flat_mappings : [];
            const units = mappings
              .map((m) => {
                const tower = m.tower_name ? String(m.tower_name) : "";
                const flat = m.flat_number || m.flat_id;
                if (!flat) return null;
                return tower ? `${tower} • ${flat}` : String(flat);
              })
              .filter(Boolean)
              .map(String);

            if (units.length === 0) {
              return <span className={styles.unassignedMuted}>—</span>;
            }

            return (
              <div className={styles.unitsRow}>
                <span className={styles.unitChip} title={units.join(", ")}>
                  <DoorOpen size={11} />
                  {units.slice(0, 2).join(", ")}
                  {units.length > 2 ? ` +${units.length - 2}` : ""}
                </span>
              </div>
            );
          },
        },
        {
          header: "Cabin / Desk",
          width: "210px",
          minWidth: "190px",
          render: (row) => {
            const deskCode = row.role_details?.desk_code;
            const cabinCode = row.role_details?.cabin_code;

            if (!deskCode && !cabinCode) {
              return <span className={styles.unassignedMuted}>—</span>;
            }

            const deskLabel = deskCode
              ? deskCode.toLowerCase().startsWith("desk") || deskCode.toLowerCase().startsWith("dsk")
                ? deskCode
                : `Desk ${deskCode}`
              : null;

            const cabinLabel = cabinCode
              ? cabinCode.toLowerCase().startsWith("cabin") || cabinCode.toLowerCase().startsWith("cbn")
                ? cabinCode
                : `Cabin ${cabinCode}`
              : null;

            return (
              <div className={styles.cabinDeskRow}>
                {deskCode && (
                  <span className={styles.deskChip} title={`Workstation / Desk: ${deskCode}`}>
                    <Laptop size={11} /> {deskLabel}
                  </span>
                )}
                {cabinCode && (
                  <span className={styles.cabinChip} title={`Cabin: ${cabinCode}`}>
                    <DoorClosed size={11} /> {cabinLabel}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          header: "Contact",
          width: "180px",
          minWidth: "160px",
          render: (row) => {
            const raw = String(row.mobile_number || "").trim();
            const formatted = formatPhoneNumber(raw);
            if (!raw) return <span className={styles.unassignedMuted}>—</span>;

            return (
              <div className={styles.contactCell}>
                <Phone size={12} color="#64748b" />
                <span className={styles.contactNumber}>{formatted}</span>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(raw);
                    toast.success(`Copied phone: ${raw}`);
                  }}
                  title="Copy Phone Number"
                >
                  <Copy size={11} />
                </button>
              </div>
            );
          },
        },
        {
          header: "Primary Role",
          width: "170px",
          minWidth: "150px",
          render: (row) => {
            const rawRole = row.primary_role || row.secondary_role || "User";
            const displayRole = formatRoleName(rawRole);
            const roleLower = String(rawRole).toLowerCase();

            const isGuard = roleLower.includes("guard") || roleLower.includes("security");
            const isAdmin =
              roleLower.includes("director") ||
              roleLower.includes("admin") ||
              roleLower.includes("head") ||
              roleLower.includes("hod") ||
              roleLower.includes("manager");
            const isResident =
              roleLower.includes("owner") ||
              roleLower.includes("tenant") ||
              roleLower.includes("resident");

            const roleBadgeClass = isGuard
              ? styles.roleBadgeSecurity
              : isAdmin
              ? styles.roleBadgeAdmin
              : isResident
              ? styles.roleBadgeResident
              : styles.roleBadgeStaff;

            const RoleIcon = isGuard ? Shield : isAdmin ? Award : isResident ? Home : Briefcase;

            return (
              <div className={styles.roleCell}>
                <span className={[styles.roleBadge, roleBadgeClass].join(" ")}>
                  <RoleIcon size={11} />
                  <span>{displayRole}</span>
                </span>
              </div>
            );
          },
        },
        {
          header: "Department & Context",
          width: "250px",
          minWidth: "220px",
          render: (row) => {
            const dept = row.role_details?.department;
            const designation = row.role_details?.designation;
            const designationCode =
              row.role_details?.designation_code ||
              row.role_details?.employee_id ||
              row.role_details?.designation_id ||
              row.role_details?.emp_id ||
              row.role_details?.code;
            const leadershipRole = row.role_details?.leadership_role;
            const familyCount = Number(row.family_count || 0);
            const roleLower = String(row.primary_role || "").toLowerCase();
            const isResident = roleLower.includes("owner") || roleLower.includes("tenant") || roleLower.includes("resident");

            const hasAny = dept || designation || designationCode || leadershipRole || (isResident && familyCount > 0);
            if (!hasAny) {
              return <span className={styles.unassignedMuted}>—</span>;
            }

            return (
              <div className={styles.deptContextCell}>
                <div className={styles.deptBadgesRow}>
                  {dept && (
                    <span className={styles.deptPill}>
                      {dept}
                    </span>
                  )}
                  {designationCode && (
                    <span className={styles.designationCodePill} title="Designation / Employee Code">
                      <Tag size={10} /> {designationCode}
                    </span>
                  )}
                </div>
                {designation && (
                  <div className={styles.designationTag}>{designation}</div>
                )}
                {leadershipRole && (
                  <div className={styles.leadershipTag}>
                    <Award size={10} /> {leadershipRole}
                  </div>
                )}
                {isResident && familyCount > 0 && (
                  <div className={styles.familyTag}>
                    <Users size={11} /> {familyCount} family {familyCount === 1 ? "member" : "members"}
                  </div>
                )}
              </div>
            );
          },
        },
        {
          header: "Vehicles",
          width: "120px",
          minWidth: "100px",
          render: (row) => {
            const count = Number(row.vehicle_count || 0);
            if (count > 0) {
              return (
                <span className={styles.vehicleCountBadge}>
                  <Car size={12} /> {count} {count === 1 ? "vehicle" : "vehicles"}
                </span>
              );
            }
            return <span className={styles.noVehicles}>—</span>;
          },
        },
        {
          header: "Status",
          width: "120px",
          minWidth: "110px",
          render: (row) => {
            if (row.approval_status === "PENDING") {
              return (
                <span className={[styles.statusPill, styles.statusPending].join(" ")}>
                  <span className={[styles.statusDot, styles.statusDotAmber].join(" ")} />
                  Pending
                </span>
              );
            }
            if (row.approval_status === "REJECTED") {
              return (
                <span className={[styles.statusPill, styles.statusInactive].join(" ")}>
                  <span className={[styles.statusDot, styles.statusDotGrey].join(" ")} />
                  Rejected
                </span>
              );
            }
            if (row.is_active === 0) {
              return (
                <span className={[styles.statusPill, styles.statusInactive].join(" ")}>
                  <span className={[styles.statusDot, styles.statusDotGrey].join(" ")} />
                  Inactive
                </span>
              );
            }
            return (
              <span className={[styles.statusPill, styles.statusActive].join(" ")}>
                <span className={[styles.statusDot, styles.statusDotGreen].join(" ")} />
                Active
              </span>
            );
          },
        },
        {
          header: "Actions",
          width: "160px",
          minWidth: "150px",
          render: (row) => (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {row.approval_status === "PENDING" && canManageResidents && (
                <>
                  <Button variant="outline" size="sm" onClick={() => handleApprove(row)} style={{ color: '#059669', borderColor: '#a7f3d0', padding: '4px 8px', fontSize: '11px', height: '28px' }}>Approve</Button>
                  <Button variant="outline" size="sm" onClick={() => handleReject(row)} style={{ color: '#dc2626', borderColor: '#fca5a5', padding: '4px 8px', fontSize: '11px', height: '28px' }}>Reject</Button>
                </>
              )}
              {canManageResidents ? (
                <ActionButtons
                  row={row}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onRequestDelete={handleDeleteClick}
                  onView={handleView}
                  showDelete={true}
                  canDelete={canDelete}
                  deleteLabel="Deactivate"
                />
              ) : (
                <Button variant="outline" size="sm" onClick={() => handleView(row)}>
                  View Details
                </Button>
              )}
            </div>
          ),
        },
      ];
    }

    return [
      ...(canDelete ? [{
        header: (
          <input
            type="checkbox"
            checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
            onChange={handleToggleSelectAll}
            title="Select All"
            style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#2563eb" }}
          />
        ),
        width: "48px",
        minWidth: "48px",
        render: (row) => (
          <input
            type="checkbox"
            checked={selectedUserIds.includes(row.user_id)}
            onChange={() => handleToggleSelectUser(row.user_id)}
            style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#2563eb" }}
          />
        ),
      }] : []),
      { header: "Name", accessor: "full_name" },
      { header: "Email ID", accessor: "email_id" },
      { header: "Phone Number", accessor: "mobile_number" },
      { header: "Gender", accessor: "gender" },
      { header: "Primary Role", render: (row) => formatRoleName(row.primary_role || row.secondary_role) },
      { header: "System Role", accessor: "system_role" },
      { header: "OS Type", accessor: "os_type" },
      ...(canPickSociety ? [{ header: "Society ID", accessor: "society_id" }] : []),
      {
        header: "Action",
        render: (row) => (
          canWriteUsers ? (
            <ActionButtons
              row={row}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onRequestDelete={handleDeleteClick}
              onView={handleView}
              showDelete={true}
              canDelete={canDelete}
              deleteLabel="Deactivate"
            />
          ) : (
            <Button variant="outline" size="sm" onClick={() => handleView(row)}>
              View Details
            </Button>
          )
        ),
      },
    ];
  }, [
    canDelete,
    canManageResidents,
    canPickSociety,
    canWriteUsers,
    effectivePropertyType,
    filteredUsers,
    handleApprove,
    handleDeleteClick,
    handleEdit,
    handleReject,
    handleToggleSelectAll,
    handleToggleSelectUser,
    handleView,
    isCommercial,
    isIndustrial,
    isResidential,
    isResidentsView,
    selectedSocietyId,
    selectedUserIds,
    societies,
    societyTypeMap,
  ]);

  return (
    <AdminLayout>
      {isResidentsView ? (
        <div className={styles.page}>
          <div className={styles.header}>
            <div>
              <h2 className={styles.pageTitle}>{displayTitle}</h2>
              <p className={styles.pageSub}>{displaySubtitle}</p>
            </div>

            {canManageResidents ? (
              <div className={styles.headerActions}>
                <Button variant="outline" icon={<Download size={16} />} onClick={exportCsv}>
                  Export
                </Button>
                <Button variant="outline" icon={<Upload size={16} />} onClick={() => navigate("/users/import")}>
                  Import
                </Button>
                <Button variant="outline" icon={<Link2 size={16} />} onClick={() => setInviteModalOpen(true)}>
                  Invite Link
                </Button>
                <Button
                  variant="primary"
                  icon={<Plus size={16} />}
                  onClick={() => {
                    setSelectedUser(null);
                    setViewMode(false);
                    setOpenModal(true);
                  }}
                >
                  Add {singularTitle}
                </Button>
              </div>
            ) : null}
          </div>

          {/* Property Type Navigation Bar */}
          <div className={styles.propertyTypeTabsBar}>
            {[
              { id: "", label: "All Properties", icon: Users, count: typeCounts.all },
              { id: "Industrial", label: "Industrial", icon: Briefcase, count: typeCounts.industrial },
              { id: "Commercial", label: "Commercial", icon: Store, count: typeCounts.commercial },
              { id: "Residential", label: "Residential", icon: Home, count: typeCounts.residential },
              { id: "Hybrid", label: "Hybrid", icon: Layers, count: typeCounts.hybrid },
            ].map((tab) => {
              const active = (typeParam || "").toLowerCase() === tab.id.toLowerCase();
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={[styles.propTabBtn, active ? styles.propTabBtnActive : ""].join(" ")}
                  onClick={() => handleSelectPropertyType(tab.id)}
                >
                  <Icon size={14} />
                  <span>{tab.label}</span>
                  <span className={styles.tabBadge}>{tab.count}</span>
                </button>
              );
            })}
          </div>

          {selectedSociety && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className={styles.activeSocietyScopeChip}>
                Scoped to Property: <strong>{selectedSociety.society_name}</strong> ({selectedSociety.society_type || "Residential"})
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedSocietyId("")}
                  style={{ background: 'none', border: 'none', padding: '0 4px', height: 'auto', minHeight: 'auto', color: '#1e40af' }}
                  title="Clear property filter"
                >
                  <X size={14} />
                </Button>
              </span>
            </div>
          )}

          <div className={styles.toolbarCard}>
            <div className={styles.searchRow}>
              <div style={{ flex: 1, minWidth: "260px" }}>
                <SearchInput
                  value={searchText}
                  onChange={(val) => setSearchText(val)}
                  placeholder={
                    isIndustrial
                      ? "Search industrial personnel by name, dept, desk code, or email..."
                      : isCommercial
                        ? "Search commercial tenants by name, unit, or email..."
                        : isResidential
                          ? "Search residents by name, flat, or email..."
                          : "Search people directory by name, property, unit, or email..."
                  }
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant={approvalFilter === "ALL" ? "primary" : "outline"}
                  onClick={() => {
                    setApprovalFilter("ALL");
                    navigate(typeParam ? `/users?type=${typeParam}` : roleParam ? `/users?role=${roleParam}` : "/users", { replace: true });
                  }}
                >
                  All
                </Button>
                <Button
                  variant={approvalFilter === "PENDING" ? "primary" : "outline"}
                  onClick={() => {
                    setApprovalFilter("PENDING");
                    navigate(typeParam ? `/users?tab=pending&type=${typeParam}` : roleParam ? `/users?tab=pending&role=${roleParam}` : "/users?tab=pending", { replace: true });
                  }}
                >
                  Pending Approvals
                </Button>
                <Button variant="outline" onClick={() => setShowFilters((v) => !v)}>
                  <Filter size={16} /> Filters
                </Button>
              </div>
            </div>

            {showFilters ? (
              <div className={styles.filtersBody}>
                <FilterBar
                  customFilters={
                    canPickSociety && (
                      <div className={styles.societyRow}>
                        <div className={styles.societyLabel}>Filter by Property</div>
                        <SelectDropdown
                          value={selectedSocietyId}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedSocietyId(value);
                            if (value) {
                              const soc = societies.find((s) => String(s.society_id) === String(value));
                              if (soc?.society_type) {
                                const params = new URLSearchParams(location.search);
                                params.set("type", soc.society_type);
                                navigate(`/users${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
                              }
                            }
                          }}
                          options={[
                            { label: "All Properties", value: "" },
                            ...scopedSocieties.map((s) => ({
                              label: `${s.society_name} (${s.society_type || "Residential"})`,
                              value: String(s.society_id),
                            })),
                          ]}
                          className={styles.societySelect}
                        />
                      </div>
                    )
                  }
                  filtersConfig={filtersConfig}
                  filters={filters}
                  setFilters={setFilters}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                  className={styles.filterBarFlat}
                />
              </div>
            ) : null}
          </div>

          <div className={styles.statsRow}>
            {roleParam === "guard" ? (
              <>
                <StatCard title="Total Guards" value={stats.total} icon={Users} iconVariant="iconBlue" />
                <StatCard title="Active Guards" value={stats.active} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="On Duty" value={stats.onDuty} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="Off Duty" value={stats.offDuty} icon={Users} iconVariant="iconPink" />
              </>
            ) : isIndustrial ? (
              <>
                <StatCard title="Total Personnel & Staff" value={stats.total} icon={Users} iconVariant="iconBlue" />
                <StatCard title="Active Staff" value={stats.active} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="Assigned Workstations" value={stats.unitsOrDesks} icon={Briefcase} iconVariant="iconPurple" />
                <StatCard title="Fleet & Vehicles" value={stats.vehicles} icon={Car} iconVariant="iconOrange" />
              </>
            ) : isCommercial ? (
              <>
                <StatCard title="Total Commercial Users" value={stats.total} icon={Users} iconVariant="iconBlue" />
                <StatCard title="Active Tenants" value={stats.active} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="Occupied Units / Shops" value={stats.unitsOrDesks} icon={Store} iconVariant="iconPurple" />
                <StatCard title="Total Vehicles" value={stats.vehicles} icon={Car} iconVariant="iconOrange" />
              </>
            ) : isResidential ? (
              <>
                <StatCard title="Total Residents" value={stats.total} icon={Users} iconVariant="iconBlue" />
                <StatCard title="Active Residents" value={stats.active} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="Family Members" value={stats.family} icon={Users} iconVariant="iconPink" />
                <StatCard title="Total Vehicles" value={stats.vehicles} icon={Car} iconVariant="iconOrange" />
              </>
            ) : (
              // Global / All Properties view
              <>
                <StatCard title="Total People" value={stats.total} icon={Users} iconVariant="iconBlue" />
                <StatCard title="Active Users" value={stats.active} icon={UserCheck} iconVariant="iconGreen" />
                <StatCard title="Units & Desks Assigned" value={stats.unitsOrDesks} icon={Building2} iconVariant="iconPurple" />
                <StatCard title="Total Vehicles" value={stats.vehicles} icon={Car} iconVariant="iconOrange" />
              </>
            )}
          </div>

          <div className={styles.tableCard}>
            <div className={styles.tableHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>All {displayTitle} ({filteredUsers.length})</span>
                {selectedUserIds.length > 0 && (
                  <span style={{ fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: '#eff6ff', color: '#2563eb' }}>
                    {selectedUserIds.length} Selected
                  </span>
                )}
              </div>
              {selectedUserIds.length > 0 && canDelete && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setIsBulkDeleteModalOpen(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 14px' }}
                  >
                    <Trash2 size={13} /> Deactivate Selected ({selectedUserIds.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedUserIds([])}
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                  >
                    Deselect All
                  </Button>
                </div>
              )}
            </div>
            <div className={styles.tableBody}>
              {loading ? (
                <div className={styles.loadingWrap}>
                  <Skeleton variant="card" count={4} />
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredUsers}
                  showSearch={false}
                  wrapCard={false}
                  minWidth="1830px"
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <DataTableLayout
          title="App User Management"
          stats={
            <div className={styles.statsRow}>
              <StatCard title="App Users" value={stats.total} />
              <StatCard title="Active App Users" value={stats.active} />
              <StatCard title="Inactive App Users" value={stats.inactive} />
            </div>
          }
          filters={
            <div className={styles.filtersColumn}>
              {canPickSociety && (
                <div className={styles.societyRow}>
                  <div className={styles.societyLabel}>Society:</div>
                  <SelectDropdown
                    value={selectedSocietyId}
                    onChange={(e) => setSelectedSocietyId(e.target.value)}
                    options={[
                      { label: "All Properties", value: "" },
                      ...societies.map((s) => ({
                        label: `${s.society_name} (${s.society_type || "Residential"})`,
                        value: String(s.society_id),
                      })),
                    ]}
                    className={styles.societySelect}
                  />
                </div>
              )}
            </div>
          }
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedUserIds.length > 0 && canDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setIsBulkDeleteModalOpen(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Trash2 size={14} /> Deactivate Selected ({selectedUserIds.length})
                </Button>
              )}
              {canWriteUsers && (
                <Button
                  variant="primary"
                  icon={<Plus size={15} />}
                  onClick={() => {
                    setSelectedUser(null);
                    setViewMode(false);
                    setOpenModal(true);
                  }}
                >
                  Add {singularTitle ? singularTitle : "User"}
                </Button>
              )}
            </div>
          }
        >
          {loading ? (
            <div className={styles.loadingWrap}>
              <Skeleton variant="card" count={4} />
            </div>
          ) : (
            <DataTable columns={columns} data={filteredUsers} minWidth="1300px" />
          )}
          <InviteUserModal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} />
      </DataTableLayout>
      )}

      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={
          viewMode || !canWriteUsers
            ? `View ${singularTitle || "User"}`
            : selectedUser
              ? `Edit ${singularTitle || "User"}`
              : `Add ${singularTitle || "User"}`
        }
      >
        <AppUserForm
          user={selectedUser}
          readOnly={viewMode || !canWriteUsers}
          formTitle={`${singularTitle || "User"} Details`}
          societies={scopedSocieties}
          defaultSocietyId={effectiveSocietyId}
          defaultRole={
            isIndustrial
              ? "Industrial Director"
              : isCommercial
                ? "Commercial Owner"
                : isHybrid
                  ? "Commercial Owner"
                  : "Owner"
          }
          propertyType={effectivePropertyType || typeParam || "Residential"}
          lockSociety={!canPickSociety}
          showFlatAssignments
          onSuccess={() => {
            setOpenModal(false);
            fetchUsers();
          }}
        />
      </Modal>

      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => !bulkDeleting && setIsBulkDeleteModalOpen(false)}
        title="Confirm Bulk Deactivation"
        size="md"
      >
        <div style={{ padding: "8px 4px" }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", marginBottom: "20px" }}>
            <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Trash2 size={22} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                Deactivate {selectedUserIds.length} {singularTitle || "User"}{selectedUserIds.length > 1 ? "s" : ""}?
              </h3>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}>
                Are you sure you want to deactivate the selected users? They will be set to inactive and will lose access to login and property operations.
              </p>
            </div>
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", marginBottom: "8px" }}>
              Selected Users to Deactivate ({selectedUserIds.length}):
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {selectedUsersList.map((u) => (
                <div key={u.user_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12.5px", padding: "6px 10px", background: "#ffffff", border: "1px solid #f1f5f9", borderRadius: "6px" }}>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{u.full_name || `User #${u.user_id}`}</span>
                  <span style={{ fontSize: "11px", color: "#64748b" }}>
                    {formatRoleName(u.primary_role || u.secondary_role)} • {u.mobile_number || u.email_id || `#${u.user_id}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button variant="outline" onClick={() => setIsBulkDeleteModalOpen(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmBulkDelete} disabled={bulkDeleting} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Trash2 size={14} />
              {bulkDeleting ? "Deactivating..." : `Yes, Deactivate (${selectedUserIds.length})`}
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "User"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Deactivate"}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );

};

export default UserManagement;
